import { Box, Stack } from '@mui/material';
import { getBusinessCessionsExpress } from 'lib/api/express';
import { Cession } from 'lib/api/express/types';
import { Filter } from 'lib/common/GlobalSearcher';
import PageHeader from 'lib/designSystem/PageHeader';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import { Header } from 'lib/designSystem/TableWithPagination';
import { errorHandler } from 'lib/functions/errors';
import { Pagination } from 'lib/types/shared';
import BusinessInvoices from 'pages/BusinessProfile/SII/Components/BusinessInvoices';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

const BASE_PAGINATION: Pagination = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 0,
};

type CessionRow = Cession & {
  cessionaryData: {
    primary: string;
    secondary: string;
  };
};

const Invoices = () => {
  const dispatch = useDispatch();
  const domain = window.location.hostname;
  const secretId = domain.split('.')[0];
  const [loading, setLoading] = useState(false);
  const [cessions, setCessions] = useState<CessionRow[]>([]);
  const [pagination, setPagination] = useState<Pagination>(BASE_PAGINATION);
  const [startDateCession, setStartDateCession] = useState<string | null>(null);
  const [endDateCession, setEndDateCession] = useState<string | null>(null);
  const [startDateIssued, setStartDateIssued] = useState<string | null>(null);
  const [endDateIssued, setEndDateIssued] = useState<string | null>(null);
  const [payerIdentifier, setPayerIdentifier] = useState('');
  const [cessionaryIdentifier, setCessionaryIdentifier] = useState('');

  const generateQuery = ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    const queryParams = new URLSearchParams();
    queryParams.set('page', String(page));
    queryParams.set('pageSize', String(pageSize));
    if (startDateCession) queryParams.set('startDateCession', startDateCession);
    if (endDateCession) queryParams.set('endDateCession', endDateCession);
    if (startDateIssued) queryParams.set('startDateIssued', startDateIssued);
    if (endDateIssued) queryParams.set('endDateIssued', endDateIssued);
    if (payerIdentifier.trim()) {
      queryParams.set('payerIdentifier', payerIdentifier.trim());
    }
    if (cessionaryIdentifier.trim()) {
      queryParams.set('cessionaryIdentifier', cessionaryIdentifier.trim());
    }
    return queryParams.toString();
  };

  const searchCessions = async ({
    page,
    pageSize,
  }: {
    page?: number;
    pageSize?: number;
  }) => {
    const nextPage = page ?? 1;
    const nextPageSize = pageSize ?? pagination.pageSize;
    setLoading(true);
    try {
      const query = generateQuery({
        page: nextPage,
        pageSize: nextPageSize,
      });
      const response = await getBusinessCessionsExpress(
        dispatch,
        query,
        secretId,
      );
      const data: CessionRow[] = (response?.data ?? []).map((cession) => ({
        ...cession,
        cessionaryData: {
          primary: cession.cessionaryIdentifier || '-',
          secondary: cession.cessionaryName || '',
        },
      }));
      const nextPagination = response?.pagination ?? BASE_PAGINATION;
      setCessions(data);
      setPagination(nextPagination);
      return { data, pagination: nextPagination };
    } catch (error) {
      errorHandler(dispatch, error, 'Error al cargar las cesiones');
      return { data: [], pagination: BASE_PAGINATION };
    } finally {
      setLoading(false);
    }
  };

  const filters: Filter[] = [
    {
      type: 'date',
      label: 'Fecha cesión',
      dateKey: 'startDateCession',
      value: startDateCession,
      setter: setStartDateCession,
    },
    {
      type: 'date',
      label: 'Fecha cesión',
      dateKey: 'endDateCession',
      value: endDateCession,
      setter: setEndDateCession,
    },
    {
      type: 'date',
      label: 'Fecha emisión',
      dateKey: 'startDateIssued',
      value: startDateIssued,
      setter: setStartDateIssued,
    },
    {
      type: 'date',
      label: 'Fecha emisión',
      dateKey: 'endDateIssued',
      value: endDateIssued,
      setter: setEndDateIssued,
    },
    {
      type: 'text',
      label: 'RUT deudor',
      value: payerIdentifier,
      setter: setPayerIdentifier,
    },
    {
      type: 'text',
      label: 'RUT cesionario',
      value: cessionaryIdentifier,
      setter: setCessionaryIdentifier,
    },
  ];

  const dataHeaders: Header[] = [
    { key: 'folio', label: 'Folio' },
    { key: 'payerIdentifier', label: 'RUT deudor' },
    { key: 'cessionaryData', label: 'Cesionario', type: 'dual-text' },
    { key: 'issuedDate', label: 'Fecha emisión', type: 'date' },
    { key: 'cessionDate', label: 'Fecha cesión', type: 'date' },
    { key: 'amount', label: 'Monto', type: 'money' },
  ];

  useEffect(() => {
    void searchCessions({ page: 1, pageSize: BASE_PAGINATION.pageSize });
  }, []);

  return (
    <Box mb={4}>
      <Box sx={{ minHeight: 340 }}>
        <BusinessInvoices />
      </Box>
      <Stack>
        <PageHeader.WithTitleAndActions
          title="Cesiones"
          subtitle="Aquí podrás ver todas las cesiones que has realizado."
          iconType="documents"
          globalSearcherProps={{
            filters,
            triggerSearch: () =>
              searchCessions({ page: 1, pageSize: pagination.pageSize }),
          }}
        />
        <TableWithPagination
          data={cessions}
          setData={setCessions}
          searchData={searchCessions}
          pagination={pagination}
          setPagination={setPagination}
          loading={loading}
          dataHeaders={dataHeaders}
        />
      </Stack>
    </Box>
  );
};

export default Invoices;
